import request from './http-request';

type UploadFunction = (file: File, ...args: any) => void;

interface WdzUploaderOptions {
  /** 平台，分为device（硬件平台）、soft（软件平台），默认为soft */
  platform?: string;
  storageType?: string;
  /** 业务code */
  businessCode?: string;
  /** 客户端: h5/web/webview */
  client: string;
  /** 企业id */
  groupId?: number;
  funtionName?: string | undefined;
  accept?: string | undefined;
  maxSize?: number | undefined;
}

interface ServerMapResponse {
  fileUploadAddress: string;
}

// 支持的文件类型
const getFileType = (extension: string) => {
  switch (extension) {
    case '.doc':
    case '.docx':
      return 'word';
    case '.xls':
    case '.xlsx':
    case '.xlsm':
      return 'excel';
    case '.ppt':
    case '.pptm':
    case '.pptx':
    case '.pot':
    case '.potm':
    case '.potx':
    case '.pps':
    case '.ppsx':
      return 'ppt';
    case '.pdf':
      return 'pdf';
    case '.txt':
      return 'txt';
    case '.png':
    case '.dng':
    case '.gif':
    case '.jpg':
    case '.jpeg':
    case '.bmp':
    case '.ico':
      return 'image';
    case '.zip':
    case '.rar':
    case '.iso':
      return 'zip';
    case '.wmv':
    case '.swf':
    case '.flv':
    case '.mp4':
    case '.rmvb':
    case '.avi':
    case '.mpg':
    case '.mkv':
    case '.mov':
    case '.mts':
      return 'video';
    case '.wav':
    case '.wma':
    case '.m4a':
    case '.w4v':
    case '.mp3':
    case '.amr':
      return 'audio';
    default:
      return 'other';
  }
};

// 格式化成年/月/日的格式
// const getCurrentDate = () => {
//   const currentDate = new Date();
//   const currentYear = currentDate.getFullYear();
//   const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
//   const currentDay = currentDate.getDate().toString().padStart(2, '0');
//   return `${currentYear}/${currentMonth}/${currentDay}`;
// };

// const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
// // 获取随机数
// const generateRandomString = (length: number) => {
//   let randomString = '';

//   for (let i = 0; i < length; i++) {
//     const randomIndex = Math.floor(Math.random() * characters.length);
//     randomString += characters.charAt(randomIndex);
//   }
//   return randomString;
// };

/** 根据文件名获取后缀，带点 */
const wdzGetFileExtension = (fileName = '') => {
  return fileName.indexOf('.') > -1 ? `.${fileName.split('.').pop()!.toLowerCase()}` : '';
};
/** 校验文件大小，单位kb */
const wdzCheckSize = (file: File, maxSize = Infinity) => {
  return file && file.size < maxSize * 1024;
};
/** 校验文件类型 */
const wdzCheckAccept = (file: File, accept = '*') => {
  if (!(file && file.name)) {
    return false;
  }
  accept = accept.toLowerCase();
  const extension = wdzGetFileExtension(file.name);
  return (
    accept === '*' ||
    new RegExp(`\\${extension}(,|$)`).test(accept) ||
    new RegExp(`${getFileType(extension)}/\\*`).test(accept)
  );
};

// 获取上传地址
let serverMap: null | ServerMapResponse = null;
const getUploadServer = async () => {
  if (serverMap) {
    return serverMap.fileUploadAddress;
  }
  const res = await request({
    method: 'GET',
    url: '/ovopark-privilege/common/getServerMap',
  });
  serverMap = res as ServerMapResponse;
  return serverMap.fileUploadAddress;
};

export default class WdzUploader {
  private platform: string;
  private storageType: string;
  private businessCode?: string;
  private client: string;
  private groupId?: number;
  private funtionName?: string;
  private accept?: string;
  private maxSize?: number;

  constructor(options: WdzUploaderOptions) {
    if (!options.client) {
      throw new Error('client is required');
    }

    this.platform = options.platform || 'soft';
    this.storageType = options.storageType || 'aliyun';
    this.businessCode = options.businessCode;
    this.client = options.client; // h5/web/webview
    this.groupId = options.groupId; // 企业id
    this.funtionName = options.funtionName; // 功能名
    this.accept = options.accept;
    this.maxSize = options.maxSize;
  }

  // 静态方法，直接用class调用
  public static checkAccept = wdzCheckAccept;
  public static checkSize = wdzCheckSize;
  public static getExtension = wdzGetFileExtension;

  //检查文件类型
  public checkAccept(file: File) {
    return wdzCheckAccept(file, this.accept);
  }

  //检查文件大小
  public checkSize(file: File) {
    return wdzCheckSize(file, this.maxSize);
  }

  // 上传文件
  public upload(
    file: File,
    config?: {
      onProgress?: UploadFunction; // 获取单个文件上传进度
      accept?: string; //可接收的文件类型
      maxSize?: number; //文件大小限制,单位kb
      businessCode?: string; // 业务code
      funtionName?: string; //功能名称
      groupId?: number; // 企业id
      fileName?: string; // 自定义文件名
      [key: string]: any;
    },
  ) {
    if (!file) {
      return Promise.reject({
        type: 'File_Missed',
        message: '缺少文件',
      });
    }

    const groupId = config?.groupId || this.groupId;
    if (!groupId) {
      return Promise.reject({
        type: 'GroupId_Missed',
        message: '缺少企业id，请在实例化或上传时指定',
      });
    }
    const businessCode = config?.businessCode || this.businessCode;
    if (!businessCode) {
      return Promise.reject({
        type: 'BusinessCode_Missed',
        message: '缺少业务code，请在实例化或上传时指定',
      });
    }
    const funtionName = config?.funtionName || this.funtionName;
    if (!funtionName) {
      return Promise.reject({
        type: 'FuntionName_Missed',
        message: '缺少功能名称，请在实例化或上传时指定',
      });
    }

    if (!wdzCheckAccept(file, config?.accept || this.accept)) {
      return Promise.reject({
        type: 'Type_Error',
        message: '文件类型不符',
      });
    }
    if (!wdzCheckSize(file, config?.maxSize || this.maxSize)) {
      return Promise.reject({
        type: 'Oversize',
        message: '文件大小超过限制',
      });
    }

    return new Promise((resolve, reject) => {
      getUploadServer()
        .then((uploadServer) => {
          request({
            method: 'POST',
            url: `${uploadServer}api/dc/storage/standard/upload`,
            form: {
              file: file,
              platform: this.platform,
              storageType: this.storageType,
              businessCode,
              enterprisePath: [groupId, this.client, funtionName].join('/'),
              customFileName: config?.fileName || '', // 自定义文件名，如果名字带后缀，直接使用这个名字，如果没有，使用file的文件类型
              uniqueName: !config?.fileName, // 未设置自定义文件名，后端会计算文件md5生成文件名
            },
            progressCallBack: (value: number) => {
              config?.onProgress?.(file, value);
            },
          })
            .then(resolve)
            .catch(reject);
        })
        .catch(reject);
    });
  }
}
